import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { checkForVersionDifferences, checkForUserDifferences, documentFolders } from '../../../../utils/contract-util';
import {
  formatDate,
  formatMetric,
  formatNumber,
  getClassNames,
  getFileExtension,
  noOp,
  toBase64,
} from '../../../../utils/util';
import { ACCEPTED, ADMIN, BUYER, CONCLUDED, PENDING, REJECTED } from '../../../../utils/constants';

import ContractDocuments from '../contract-documents/ContractDocuments';
import DatePicker from '../../inputs/date-picker/DatePicker';
import TextInput from '../../inputs/text-input/TextInput';
import Button from '../../button/Button';
import ImageCarousel from '../../image-carousel/ImageCarousel';

import './contract-terms.scss';

const ContractTerms = ({
  dealNumber,
  currentVersion,
  previousVersion,
  product,
  role,
  buyer,
  seller,
  latest,
  amendContract,
  updateContractStatus,
  downloadDocument,
  cancelContract,
  reviewDocument,
  showPopup,
}) => {
  const { profile } = useSelector(({ party }) => party);

  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(undefined);
  const [folders, setFolders] = useState(documentFolders);
  const [editingQuantity, setEditingQuantity] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [editingCity, setEditingCity] = useState(false);
  const [editingCountry, setEditingCountry] = useState(false);
  const [editingDeliveryDate, setEditingDeliveryDate] = useState(false);
  const [differences, setDifferences] = useState({});
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);
  const [canUserEdit, setCanUserEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedImageIndex, setSelectedImageIndex] = useState(undefined);

  const quantityRef = useRef();
  const priceRef = useRef();
  const cityRef = useRef();
  const countryRef = useRef();
  const deliveryDateRef = useRef();

  useEffect(() => {
    if (currentVersion && product) {
      setQuantity(formatNumber(currentVersion.quantity));
      setPrice(formatNumber(currentVersion.price));
      setCity(currentVersion.city ?? '');
      setCountry(currentVersion.country ?? '');

      if (currentVersion.deliveryDate) {
        setDeliveryDate(new Date(currentVersion.deliveryDate));
      }

      const copyOfFolders = structuredClone(documentFolders);
      for (const documentRequirement of currentVersion.documentRequirements) {
        const folder = copyOfFolders.find((folder) => folder.name === documentRequirement.category);
        const category = folder.categories.find((category) => category.name === documentRequirement.name);
        category.status = documentRequirement.required;
      }

      for (const document of currentVersion.documents) {
        const [folderName, categoryName] = document.subject.split('_$');
        const folder = copyOfFolders.find((folder) => folder.name === folderName);
        const category = folder.categories.find((category) => category.name === categoryName);
        category.documents.push(document);
      }

      setFolders(copyOfFolders);
    }
  }, [currentVersion, product]);

  useEffect(() => {
    const currentVersionStatus = currentVersion.status.toLowerCase();
    if (
      !latest ||
      currentVersionStatus === CONCLUDED ||
      currentVersionStatus === REJECTED ||
      currentVersionStatus === ACCEPTED ||
      role === ADMIN
    ) {
      setCanUserEdit(false);
      return;
    }

    setCanUserEdit(true);
  }, [buyer, seller, latest, currentVersion, profile, role]);

  useEffect(() => {
    if (!previousVersion) return;

    const differences = checkForVersionDifferences(previousVersion, quantity, price, city, country, deliveryDate);
    setDifferences(differences);
  }, [previousVersion, city, country, deliveryDate, price, quantity]);

  useEffect(() => {
    if (!currentVersion) return;

    const hasUserMadeChanges = checkForUserDifferences(
      currentVersion,
      quantity,
      price,
      city,
      country,
      deliveryDate,
      folders
    );
    setHasUserMadeChanges(hasUserMadeChanges);
    showPopup.current = hasUserMadeChanges;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVersion, city, country, deliveryDate, price, quantity, folders]);

  useEffect(() => {
    if (editingQuantity && quantityRef.current) {
      quantityRef.current.focus();
    }
  }, [editingQuantity]);

  useEffect(() => {
    if (editingPrice && priceRef.current) {
      priceRef.current.focus();
    }
  }, [editingPrice]);

  useEffect(() => {
    if (editingCity && cityRef.current) {
      cityRef.current.focus();
    }
  }, [editingCity]);

  useEffect(() => {
    if (editingCountry && countryRef.current) {
      countryRef.current.focus();
    }
  }, [editingCountry]);

  useEffect(() => {
    if (editingDeliveryDate && deliveryDateRef.current) {
      deliveryDateRef.current.setFocus();
    }
  }, [editingDeliveryDate]);

  const addDocument = (folderName, categoryName, document) => {
    const copyOfFolders = structuredClone(folders);
    const folder = copyOfFolders.find((folder) => folder.name === folderName);
    const category = folder.categories.find((category) => category.name === categoryName);
    category.documents.push(document);

    setFolders(copyOfFolders);
  };

  const updateCategoryStatus = (folderName, categoryName, status) => {
    const copyOfFolders = structuredClone(folders);
    const folder = copyOfFolders.find((folder) => folder.name === folderName);
    const category = folder.categories.find((category) => category.name === categoryName);
    category.status = status;

    setFolders(copyOfFolders);
  };

  const updateDocumentStatus = (folderName, categoryName, documentExternalReference, status, facilitator) => {
    const copyOfFolders = structuredClone(folders);
    const folder = copyOfFolders.find((folder) => folder.name === folderName);
    const category = folder.categories.find((category) => category.name === categoryName);
    const document = category.documents.find((document) => document.externalReference === documentExternalReference);

    if (facilitator) {
      document.atpStatus = status;
    } else {
      document.buyerStatus = status;
    }

    setFolders(copyOfFolders);
  };

  const deleteDocument = (folderName, categoryName, index) => {
    const copyOfFolders = structuredClone(folders);
    const folder = copyOfFolders.find((folder) => folder.name === folderName);
    const category = folder.categories.find((category) => category.name === categoryName);
    category.documents.splice(index, 1);

    setFolders(copyOfFolders);
  };

  const calculateTotalPrice = (price, quantity) => {
    if (!quantity || !price) return 0;

    return quantity * (price / product.productUnit);
  };

  const validateFields = () => {
    const errors = {};

    if (!quantity) errors.quantity = true;
    if (!price) errors.price = true;
    if (!city) errors.city = true;
    if (!country) errors.country = true;
    if (!deliveryDate) errors.deliveryDate = true;

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitForConsiderationClick = async () => {
    const valid = validateFields();
    if (!valid) return;

    const contractDocumentRequirements = [];

    for (const folder of folders) {
      const { categories } = folder;

      for (const category of categories) {
        contractDocumentRequirements.push({
          name: category.name,
          category: folder.name,
          required: category.status,
        });
      }
    }

    const mappedDocuments = [];

    for (const folder of folders) {
      for (const category of folder.categories) {
        for (const document of category.documents) {
          if (!document.externalReference) {
            mappedDocuments.push({
              name: document.name,
              subject: `${folder.name}_$${category.name}`,
              base64String: await toBase64(document),
              documentExtension: getFileExtension(document.name),
              status: 'Pending',
            });
          } else {
            mappedDocuments.push({
              name: document.name,
              subject: `${folder.name}_$${category.name}`,
              linkedMediaExternalReference: document.externalReference,
              documentExtension: getFileExtension(document.name),
              status: document.buyerStatus,
            });
          }
        }
      }
    }

    const contract = {
      desiredQuantity: quantity,
      desiredPricePerProductUnit: price,
      destinationCity: city,
      destinationCountry: country,
      expectedDeliveryDate: deliveryDate,
      documents: mappedDocuments,
      contractDocumentRequirements,
      dealNumber,
    };

    amendContract(contract);
  };

  const handleQuantityChange = (event) => {
    if (Number(event.target.value) > product.availableQuantity) {
      setQuantity(Math.round(product.availableQuantity));
    } else {
      setQuantity(event.target.value);
    }
  };

  const renderOpeningNegotiationFields = () => (
    <div className="contract-terms__grid">
      <div className="contract-terms__grid-item">
        <label htmlFor="quantity">Desired Quantity Is</label>
        <div className={getClassNames('contract-terms__toggle-input', { short: true })}>
          <TextInput
            ref={quantityRef}
            name="quantity"
            value={quantity}
            onChange={handleQuantityChange}
            error={errors.quantity}
            numberOnly
          />
          <p>{formatMetric(product.productUnitMetric, quantity)}</p>
        </div>
      </div>

      <div className="contract-terms__grid-item">
        <label htmlFor="price">Desired Price Is</label>
        <div className={getClassNames('contract-terms__toggle-input', { short: true })}>
          <TextInput
            ref={priceRef}
            name="price"
            value={price}
            onChange={({ target }) => setPrice(target.value)}
            error={errors.price}
            numberOnly
          />
          <p>
            <sup>USD</sup> per {formatNumber(product.productUnit)}{' '}
            {formatMetric(product.productUnitMetric, product.productUnit)}
          </p>
        </div>
      </div>

      <span />

      <div className="contract-terms__grid-item">
        <p>Total Offered Price Is</p>
        <p>
          {formatNumber(calculateTotalPrice(price, quantity))}
          <sup>USD</sup>
        </p>
      </div>

      <div className="contract-terms__grid-item">
        <label htmlFor="city">Destination City</label>
        <TextInput name="city" value={city} onChange={({ target }) => setCity(target.value)} error={errors.city} />
      </div>

      <div className="contract-terms__grid-item">
        <label htmlFor="country">Destination Country</label>
        <TextInput
          name="country"
          value={country}
          onChange={({ target }) => setCountry(target.value)}
          error={errors.country}
        />
      </div>

      <div className="contract-terms__grid-item">
        <label htmlFor="delivery-date">Delivery Expected By</label>
        <DatePicker
          name="delivery-date"
          value={deliveryDate}
          onChange={(date) => setDeliveryDate(date)}
          error={errors.deliveryDate}
        />
      </div>
    </div>
  );

  const renderFields = () => (
    <div className="contract-terms__grid">
      <div className="contract-terms__grid-item">
        <p>Product Name</p>
        <div className="contract-terms__toggle-input">
          <p>{product.name}</p>
        </div>
      </div>

      <span />

      <div className="contract-terms__grid-item">
        <label htmlFor="quantity">Desired Quantity Is</label>
        <div
          className={getClassNames('contract-terms__toggle-input', {
            different: differences.quantity && !editingQuantity,
            short: true,
          })}
        >
          {editingQuantity ? (
            <>
              <TextInput
                ref={quantityRef}
                name="quantity"
                value={quantity}
                onChange={handleQuantityChange}
                error={errors.quantity}
                numberOnly
              />
              <p>{formatMetric(product.productUnitMetric, quantity)}</p>
            </>
          ) : (
            <p>
              {quantity} {formatMetric(product.productUnitMetric, quantity)}
            </p>
          )}
          {canUserEdit && !editingQuantity && (
            <button className="contract-terms__edit-button" onClick={() => setEditingQuantity(true)}>
              change quantity
            </button>
          )}
        </div>
      </div>

      <div className="contract-terms__grid-item">
        <p>Offered Price Is</p>
        <div
          className={getClassNames('contract-terms__toggle-input', {
            different: differences.price || differences.quantity,
          })}
        >
          <p>
            {formatNumber(calculateTotalPrice(currentVersion.price, quantity))}
            <sup>USD</sup> ({formatNumber(currentVersion.price)}
            <sup>USD</sup> per {formatNumber(product.productUnit)}{' '}
            {formatMetric(product.productUnitMetric, product.productUnit)})
          </p>
          {canUserEdit && !editingPrice && (
            <button className="contract-terms__edit-button" onClick={() => setEditingPrice(true)}>
              change price
            </button>
          )}
        </div>
      </div>

      {editingPrice && (
        <>
          <span />
          <div className="contract-terms__grid-item">
            <label htmlFor="price">Acceptable Price Is</label>
            <div className={getClassNames('contract-terms__toggle-input', { short: true })}>
              <TextInput
                ref={priceRef}
                name="price"
                value={price}
                onChange={({ target }) => setPrice(target.value)}
                error={errors.price}
                numberOnly
              />
              <p>
                <sup>USD</sup> per {formatNumber(product.productUnit)}{' '}
                {formatMetric(product.productUnitMetric, product.productUnit)}
              </p>
            </div>
          </div>
          <span />
          <div className="contract-terms__grid-item">
            <p>Total Price Is</p>
            <div className="contract-terms__toggle-input">
              <p>
                {formatNumber(calculateTotalPrice(price, quantity))}
                <sup>USD</sup>
              </p>
            </div>
          </div>
        </>
      )}

      <div className="contract-terms__grid-item">
        <label htmlFor="city">Destination City</label>
        <div className={getClassNames('contract-terms__toggle-input', { different: differences.city })}>
          {editingCity ? (
            <TextInput
              ref={cityRef}
              name="city"
              value={city}
              onChange={({ target }) => setCity(target.value)}
              error={errors.city}
            />
          ) : (
            <p>{city}</p>
          )}
          {canUserEdit && !editingCity && (
            <button className="contract-terms__edit-button" onClick={() => setEditingCity(true)}>
              change city
            </button>
          )}
        </div>
      </div>

      <div className="contract-terms__grid-item">
        <label htmlFor="country">Destination Country</label>
        <div className={getClassNames('contract-terms__toggle-input', { different: differences.country })}>
          {editingCountry ? (
            <TextInput
              ref={countryRef}
              name="country"
              value={country}
              onChange={({ target }) => setCountry(target.value)}
              error={errors.country}
            />
          ) : (
            <p>{country}</p>
          )}
          {canUserEdit && !editingCountry && (
            <button className="contract-terms__edit-button" onClick={() => setEditingCountry(true)}>
              change country
            </button>
          )}
        </div>
      </div>

      <div className="contract-terms__grid-item">
        <label htmlFor="delivery-date">Delivery Expected By</label>
        <div className={getClassNames('contract-terms__toggle-input', { different: differences.deliveryDate })}>
          {editingDeliveryDate ? (
            <DatePicker
              ref={deliveryDateRef}
              name="delivery-date"
              value={deliveryDate}
              onChange={(date) => setDeliveryDate(date)}
              error={errors.deliveryDate}
            />
          ) : (
            <p>{formatDate(deliveryDate)}</p>
          )}
          {canUserEdit && !editingDeliveryDate && (
            <button className="contract-terms__edit-button" onClick={() => setEditingDeliveryDate(true)}>
              change date
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderDocumentFolders = () => {
    return folders.map((folder) => (
      <ContractDocuments
        key={folder.name}
        folder={folder}
        role={role}
        canUserEdit={canUserEdit}
        addDocument={addDocument}
        updateCategoryStatus={updateCategoryStatus}
        updateDocumentStatus={updateDocumentStatus}
        deleteDocument={deleteDocument}
        downloadDocument={downloadDocument}
        reviewDocument={reviewDocument}
        latest={latest}
      />
    ));
  };

  const renderSecondaryButton = () => {
    if (!latest || currentVersion.status.toLowerCase() === ACCEPTED) return null;

    if (currentVersion.version === '0') {
      return (
        <Button onClick={() => cancelContract(dealNumber)} secondary>
          CANCEL
        </Button>
      );
    }

    return (
      <Button onClick={() => updateContractStatus(dealNumber, REJECTED)} secondary>
        REJECT
      </Button>
    );
  };

  const renderPrimaryButton = () => {
    const currentVersionStatus = currentVersion.status.toLowerCase();

    if (!latest || (currentVersionStatus === ACCEPTED && role !== ADMIN)) return null;

    if (currentVersionStatus === ACCEPTED && role === ADMIN) {
      return <Button onClick={() => updateContractStatus(dealNumber, CONCLUDED)}>CONCLUDE DEAL</Button>;
    }

    if (
      currentVersion.organisationExternalReference !== profile.organisationExternalReference &&
      PENDING === currentVersionStatus &&
      !hasUserMadeChanges
    ) {
      return <Button onClick={() => updateContractStatus(dealNumber, ACCEPTED)}>ACCEPT TERMS</Button>;
    }

    return (
      <Button onClick={handleSubmitForConsiderationClick} disabled={!hasUserMadeChanges}>
        SUBMIT FOR CONSIDERATION
      </Button>
    );
  };

  return (
    <>
      <div className="contract-terms">
        <p className="contract-terms__title">Proposed Terms of Agreement</p>
        {!previousVersion && role === BUYER ? renderOpeningNegotiationFields() : renderFields()}

        <div className="contract-terms__buttons">
          {renderSecondaryButton()}
          {renderPrimaryButton()}
        </div>

        <div className="contract-terms__document-folders">
          <h2 className="contract-terms__document-folders__heading">Document Folders</h2>
          <p className="contract-terms__document-folders__disclaimer">
            The list of documents below includes all the possible files that might be needed during the negotiation
            process.
          </p>
          <p className="contract-terms__document-folders__disclaimer contract-terms__document-folders__disclaimer--bold">
            Please Note:
          </p>
          <ul className="contract-terms__document-folders__disclaimer__list">
            <li className="contract-terms__document-folders__disclaimer contract-terms__document-folders__disclaimer--bold">
              You do not need to upload all documents at once.
            </li>
            <li className="contract-terms__document-folders__disclaimer contract-terms__document-folders__disclaimer--bold">
              Only upload the documents relevant to your current negotiation stage.
            </li>
          </ul>
          <p className="contract-terms__document-folders__disclaimer">
            This list is to help you understand the types of documents you and the other party may need as you move
            forward.
          </p>
          <p className="contract-terms__document-folders__disclaimer">For any questions, please contact support.</p>
          {renderDocumentFolders()}
        </div>

        <div className="contract-terms__buttons">
          {renderSecondaryButton()}
          {renderPrimaryButton()}
        </div>
      </div>

      {selectedImageIndex !== undefined && (
        <ImageCarousel images={product?.photos} startIndex={selectedImageIndex} setStartIndex={setSelectedImageIndex} />
      )}
    </>
  );
};

ContractTerms.propTypes = {
  dealNumber: PropTypes.string.isRequired,
  currentVersion: PropTypes.shape({}).isRequired,
  previousVersion: PropTypes.shape({}),
  product: PropTypes.shape({}).isRequired,
  role: PropTypes.string.isRequired,
  buyer: PropTypes.shape({}).isRequired,
  seller: PropTypes.shape({}).isRequired,
  latest: PropTypes.bool,
  amendContract: PropTypes.func.isRequired,
  updateContractStatus: PropTypes.func,
  downloadDocument: PropTypes.func,
  cancelContract: PropTypes.func,
  reviewDocument: PropTypes.func,
  showPopup: PropTypes.shape({}).isRequired,
};

ContractTerms.defaultProps = {
  previousVersion: undefined,
  latest: false,
  updateContractStatus: noOp,
  downloadDocument: noOp,
  cancelContract: noOp,
  reviewDocument: noOp,
};

export default ContractTerms;
